import { createEffect, onCleanup } from 'solid-js';
import PublicLayout from './PublicLayout';
import { t, locale } from '@/shared/i18n';

export default function PrivacyPolicyPage() {
  createEffect(() => {
    const lang = locale() === 'vi' ? 'vi' : 'en';
    const path = lang === 'vi' ? '/vi/privacy' : '/privacy';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: t('privacy.title'),
      description: t('seo.privacy.desc'),
      inLanguage: lang,
      url: `https://khoai.io${path}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
    };

    let script = document.getElementById(
      'ld-json-privacy-policy'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-privacy-policy';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
  });

  onCleanup(() => {
    document.getElementById('ld-json-privacy-policy')?.remove();
  });

  const link = 'text-blue-600 hover:underline';
  const sectionCls = 'mt-10';
  const h2Cls = 'text-xl font-bold text-text-primary';
  const h3Cls = 'mt-4 text-base font-semibold text-text-primary';
  const pCls = 'mt-3 text-sm leading-relaxed text-text-secondary';
  const p2Cls = 'mt-2 text-sm leading-relaxed text-text-secondary';
  const ulCls = 'mt-3 list-disc space-y-1 pl-6 text-sm text-text-secondary';
  const ul2Cls = 'mt-2 list-disc space-y-1 pl-6 text-sm text-text-secondary';

  const renderLabeledItem = (key: string) => {
    const value = t(key);
    const [label, ...rest] = value.split(' — ');
    const desc = rest.join(' — ');
    return (
      <li>
        <strong>{label}</strong>
        {desc ? <> — {desc}</> : null}
      </li>
    );
  };

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 class="text-3xl font-extrabold text-text-primary sm:text-4xl">
          {t('privacy.title')}
        </h1>
        <p class="mt-2 text-sm text-text-secondary">
          {t('privacy.lastUpdated')}
        </p>

        {/* 1. Introduction */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s1.title')}</h2>
          <p class={pCls}>
            {t('privacy.s1.p1')}{' '}
            <a href="https://khoai.io" class={link}>
              khoai.io
            </a>
            .
          </p>
          <p class={p2Cls}>{t('privacy.s1.p2')}</p>
        </section>

        {/* 2. Information We Collect */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s2.title')}</h2>

          <h3 class={h3Cls}>{t('privacy.s2.accountData')}</h3>
          <ul class={ul2Cls}>
            <li>{t('privacy.s2.account.li1')}</li>
            <li>{t('privacy.s2.account.li2')}</li>
            <li>{t('privacy.s2.account.li3')}</li>
            <li>{t('privacy.s2.account.li4')}</li>
          </ul>

          <h3 class={h3Cls}>{t('privacy.s2.businessData')}</h3>
          <ul class={ul2Cls}>
            <li>{t('privacy.s2.business.li1')}</li>
            <li>{t('privacy.s2.business.li2')}</li>
            <li>{t('privacy.s2.business.li3')}</li>
          </ul>

          <h3 class={h3Cls}>{t('privacy.s2.inventoryData')}</h3>
          <ul class={ul2Cls}>
            <li>{t('privacy.s2.inventory.li1')}</li>
            <li>{t('privacy.s2.inventory.li2')}</li>
            <li>{t('privacy.s2.inventory.li3')}</li>
            <li>{t('privacy.s2.inventory.li4')}</li>
          </ul>

          <h3 class={h3Cls}>{t('privacy.s2.usageData')}</h3>
          <ul class={ul2Cls}>
            <li>{t('privacy.s2.usage.li1')}</li>
            <li>{t('privacy.s2.usage.li2')}</li>
            <li>{t('privacy.s2.usage.li3')}</li>
            <li>{t('privacy.s2.usage.li4')}</li>
          </ul>

          <h3 class={h3Cls}>{t('privacy.s2.cookiesData')}</h3>
          <ul class={ul2Cls}>
            <li>{t('privacy.s2.cookies.li1')}</li>
            <li>{t('privacy.s2.cookies.li2')}</li>
          </ul>
        </section>

        {/* 3. How We Use Your Information */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s3.title')}</h2>
          <ul class={ulCls}>
            <li>{t('privacy.s3.li1')}</li>
            <li>{t('privacy.s3.li2')}</li>
            <li>{t('privacy.s3.li3')}</li>
            <li>{t('privacy.s3.li4')}</li>
            <li>{t('privacy.s3.li5')}</li>
            <li>{t('privacy.s3.li6')}</li>
          </ul>
        </section>

        {/* 4. Legal Basis for Processing (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s4.title')}</h2>
          <p class={pCls}>{t('privacy.s4.p1')}</p>
          <ul class={ul2Cls}>
            <li>{t('privacy.s4.li1')}</li>
            <li>{t('privacy.s4.li2')}</li>
            <li>{t('privacy.s4.li3')}</li>
            <li>{t('privacy.s4.li4')}</li>
          </ul>
        </section>

        {/* 5. Data Sharing */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s5.title')}</h2>
          <p class={pCls}>{t('privacy.s5.p1')}</p>
          <p class={p2Cls}>{t('privacy.s5.p2')}</p>
          <ul class={ul2Cls}>
            {renderLabeledItem('privacy.s5.li1')}
            {renderLabeledItem('privacy.s5.li2')}
            {renderLabeledItem('privacy.s5.li3')}
            {renderLabeledItem('privacy.s5.li4')}
            {renderLabeledItem('privacy.s5.li5')}
          </ul>
          <p class={p2Cls}>{t('privacy.s5.p3')}</p>
        </section>

        {/* 6. International Data Transfers (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s6.title')}</h2>
          <p class={pCls}>{t('privacy.s6.p1')}</p>
          <p class={p2Cls}>{t('privacy.s6.p2')}</p>
        </section>

        {/* 7. Data Security */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s7.title')}</h2>
          <p class={pCls}>{t('privacy.s7.p1')}</p>
          <ul class={ul2Cls}>
            <li>{t('privacy.s7.li1')}</li>
            <li>{t('privacy.s7.li2')}</li>
            <li>{t('privacy.s7.li3')}</li>
            <li>{t('privacy.s7.li4')}</li>
            <li>{t('privacy.s7.li5')}</li>
            <li>{t('privacy.s7.li6')}</li>
          </ul>
        </section>

        {/* 8. Data Retention */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s8.title')}</h2>
          <ul class={ulCls}>
            <li>{t('privacy.s8.li1')}</li>
            <li>{t('privacy.s8.li2')}</li>
            <li>{t('privacy.s8.li3')}</li>
          </ul>
        </section>

        {/* 9. Your Rights (expanded) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s9.title')}</h2>
          <p class={pCls}>{t('privacy.s9.p1')}</p>
          <ul class={ul2Cls}>
            <li>
              <strong>{t('privacy.s9.li1').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li1').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li2').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li2').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li3').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li3').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li4').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li4').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li5').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li5').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li6').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li6').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s9.li7').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s9.li7').split(' — ')[1]}
            </li>
          </ul>
        </section>

        {/* 10. Cookies */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s10.title')}</h2>
          <p class={pCls}>{t('privacy.s10.p1')}</p>
          <ul class={ul2Cls}>
            <li>
              <strong>{t('privacy.s10.li1').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s10.li1').split(' — ')[1]}
            </li>
            <li>
              <strong>{t('privacy.s10.li2').split(' — ')[0]}</strong> —{' '}
              {t('privacy.s10.li2').split(' — ')[1]}
            </li>
          </ul>
          <p class={p2Cls}>{t('privacy.s10.p2')}</p>
        </section>

        {/* 11. Children */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s11.title')}</h2>
          <p class={pCls}>
            {t('privacy.s11.p1')}{' '}
            <a href="mailto:human.khoai@gmail.com" class={link}>
              contact us
            </a>{' '}
            {t('privacy.s11.p1.after')}
          </p>
        </section>

        {/* 12. Data Breach Notification (NEW) */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s12.title')}</h2>
          <p class={pCls}>{t('privacy.s12.p1')}</p>
        </section>

        {/* 13. Changes to This Policy */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s13.title')}</h2>
          <p class={pCls}>{t('privacy.s13.p1')}</p>
        </section>

        {/* 14. Contact */}
        <section class={sectionCls}>
          <h2 class={h2Cls}>{t('privacy.s14.title')}</h2>
          <p class={pCls}>{t('privacy.s14.p1')}</p>
          <p class="mt-2 text-sm text-text-secondary">
            <a href="mailto:human.khoai@gmail.com" class={link}>
              contact us
            </a>
          </p>
        </section>
      </article>
    </PublicLayout>
  );
}
